import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Smartphone } from "lucide-react";

const errText = (d) => (typeof d === "string" ? d : d?.msg || "Something went wrong. Please try again.");

// Blocks the app until a freshly-signed-in Google user verifies their mobile once.
export default function MobileVerifyGate() {
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const needs = user && user.mobile_verified === false;
  if (!needs) return null;

  const sendOtp = async () => {
    if (mobile.trim().length !== 10) { toast.error("Enter a valid 10-digit mobile number."); return; }
    setBusy(true);
    try {
      await api.post("/auth/mobile/request-otp", { mobile: mobile.trim() });
      setStep("otp"); toast.success("OTP sent to your mobile");
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    if (code.trim().length < 4) { toast.error("Enter the OTP"); return; }
    setBusy(true);
    try {
      await api.post("/auth/mobile/verify-otp", { mobile: mobile.trim(), code: code.trim() });
      toast.success("Mobile verified!");
      await checkAuth();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true}>
      <DialogContent className="rounded-2xl max-w-md [&>button]:hidden" data-testid="mobile-verify-gate">
        <DialogHeader>
          <div className="h-12 w-12 rounded-xl bg-[#0E9AC8]/10 flex items-center justify-center mb-2"><Smartphone className="h-6 w-6 text-[#0E9AC8]" /></div>
          <DialogTitle className="font-display text-xl">Verify your mobile number</DialogTitle>
          <DialogDescription>One-time step to secure your account. You won't be asked again.</DialogDescription>
        </DialogHeader>
        {step === "mobile" ? (
          <div className="space-y-4">
            <div>
              <Label>Mobile Number</Label>
              <Input data-testid="gate-mobile" className="mt-1.5 rounded-xl" placeholder="10-digit Indian mobile"
                value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} onKeyDown={(e) => e.key === "Enter" && sendOtp()} />
            </div>
            <Button data-testid="gate-send" onClick={sendOtp} disabled={busy} className="w-full h-11 rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-2" /> Send OTP</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Enter the 6-digit OTP sent to <b>{mobile}</b>.</p>
            <Input data-testid="gate-otp" className="rounded-xl text-center tracking-[0.4em] text-lg" placeholder="••••••"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => e.key === "Enter" && verify()} />
            <Button data-testid="gate-verify" onClick={verify} disabled={busy} className="w-full h-11 rounded-xl bg-[#0E9AC8] hover:bg-[#0B7EA4]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Continue"}
            </Button>
            <button onClick={() => setStep("mobile")} className="w-full text-xs text-slate-500 hover:text-[#0E9AC8]">← Change number</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
